import React, { Fragment, useCallback, useMemo } from 'react';

import qs from 'qs';
import { useDispatch } from 'react-redux';
import { useQueryParam, useResource } from 'react-resource-router';

import Button from '@atlaskit/button/standard-button';
import { Grid, GridColumn } from '@atlaskit/page';

import { FocusedPageHeader } from 'src/components/accessibility';
import { Pagination } from 'src/components/pagination';
import { SCROLL_TO_TOP_DURATION } from 'src/constants/permalink-scroll';
import { useIntl } from 'src/hooks/intl';
import { WORKSPACE_PROJECTS_LIST_LOAD, MetricSender } from 'src/metrics';
import scrollTo from 'src/redux/global/actions/scroll-to';
import { useLocation } from 'src/router/hooks';
import Breadcrumbs from 'src/sections/workspace/components/breadcrumbs';
import { useWorkspaceNavActionUrls } from 'src/sections/workspace/components/navigation/use-container-navigation-props';
import { Page } from 'src/types/pagination';
import { getPagination } from 'src/utils/get-pagination';

import Filter from './containers/filter';
import { ProjectTable } from './project-table';
import messages from './projects.i18n';
import { workspaceProjectsResource } from './workspace-projects-resource';

const ProjectListPage = (): JSX.Element => {
  const { formatMessage } = useIntl();
  const { createProjectUrl } = useWorkspaceNavActionUrls();

  const { data, loading, error } = useResource(workspaceProjectsResource);
  const { projects, size, pagelen } = data || {};
  const { pathname, search } = useLocation();
  const [page] = useQueryParam('page');
  const dispatch = useDispatch();

  const getPaginationUrl = useCallback(
    (selectedPage: number) => {
      const queryParams = qs.parse(search, { ignoreQueryPrefix: true });
      const newQueryString = qs.stringify({
        ...queryParams,
        page: selectedPage,
      });
      return `${pathname}?${newQueryString}`;
    },
    [pathname, search]
  );

  const getPages = useMemo(() => {
    return size && pagelen ? Math.ceil(size / pagelen) : 0;
  }, [size, pagelen]);

  const onPageChange = useCallback(
    () =>
      dispatch(
        scrollTo({
          targetId: 'root',
          duration: SCROLL_TO_TOP_DURATION,
        })
      ),
    [dispatch]
  );

  const renderPagination = () => {
    const pages: Page[] = getPagination(getPages)(getPaginationUrl);
    const parsedPage = page ? parseInt(page, 10) : 1;
    return (
      <Fragment>
        {projects && (
          <div data-testid="workspace-pagination">
            <Pagination
              isError={!!error}
              isLoading={loading}
              onPageChange={onPageChange}
              page={!isNaN(parsedPage) ? Math.max(parsedPage, 1) : 1}
              pages={pages}
              uiEventSource="WorkspaceProjectScreen"
              uiEventSubjectId="WorkspaceProjectPagination"
            />
          </div>
        )}
      </Fragment>
    );
  };

  // "Create project" button will only show if user has access
  const action = useMemo(() => {
    return createProjectUrl ? (
      <Button href={createProjectUrl}>
        {formatMessage(messages.projectCreate)}
      </Button>
    ) : undefined;
  }, [createProjectUrl, formatMessage]);

  return (
    <Grid layout="fluid" spacing="comfortable">
      <GridColumn>
        <FocusedPageHeader actions={action} breadcrumbs={<Breadcrumbs />}>
          {formatMessage(messages.projectsPageHeader)}
        </FocusedPageHeader>
        <Filter />
        <ProjectTable
          projects={projects}
          renderPagination={renderPagination}
          isError={!!error}
          isLoading={loading}
          isFiltering={!!search}
        />
      </GridColumn>
      {!loading && <MetricSender metric={WORKSPACE_PROJECTS_LIST_LOAD} />}
    </Grid>
  );
};

export default ProjectListPage;
